<script setup>
// NOOVI: resumo de SLA na Overview — mesma fonte que /reports/sla já usa
// (slaReports/getMetrics → applied_slas#metrics). O controller original é
// admin-only (before_action :check_admin_authorization), então este card só
// é montado quando useAdmin().isAdmin é true (ver LiveReports.vue) — nunca
// tenta renderizar um estado quebrado para agente comum.
import { computed, inject, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { DonutChart } from '@chatwoot/viz';
import { useStore, useMapGetter } from 'dashboard/composables/store';
// NOOVI: reaproveita o tile atômico (SLAMetricCard), não o wrapper
// SLAMetrics.vue inteiro — SLAMetrics já traz sua própria moldura de card
// (shadow/outline/bg), que dentro do OverviewCard duplicaria a borda. O
// gauge radial (pedido no mockup) reaproveita o MESMO DonutChart de
// ChannelSummaryContainer.vue com um segmento só — a lib já resolve "usado
// vs. resto" via `total`, então não precisou de SVG feito à mão.
import SLAMetricCard from './SLA/SLAMetricCard.vue';
import OverviewCard from './overview/MetricCard.vue';
import {
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
} from '../helpers/overviewDateFilter';

const { t } = useI18n();
const store = useStore();
const dateFilter = inject(
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
  true
);

const metrics = useMapGetter('slaReports/getMetrics');
const uiFlags = useMapGetter('slaReports/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetchingMetrics);

const hasConversations = computed(
  () => metrics.value.numberOfConversations > 0
);

const gaugeData = computed(() => ({
  segments: [
    {
      id: 'hit',
      value: Math.max(
        metrics.value.numberOfConversations - metrics.value.numberOfSLAMisses,
        0
      ),
      color: 'rgb(var(--teal-9))',
    },
  ],
  total: metrics.value.numberOfConversations,
}));

// slaReports/getMetrics não aceita business_hours (a API de SLA não filtra
// por horário comercial) — o watch só reage a from/to de propósito, pra não
// refazer a mesma chamada quando só o toggle de horário comercial muda.
const fetchSlaMetrics = () => {
  store.dispatch('slaReports/getMetrics', {
    from: dateFilter.from,
    to: dateFilter.to,
  });
};

onMounted(fetchSlaMetrics);
watch(() => [dateFilter.from, dateFilter.to], fetchSlaMetrics);
</script>

<template>
  <OverviewCard
    :is-loading="isLoading"
    :loading-message="t('OVERVIEW_REPORTS.SLA_SUMMARY.LOADING_MESSAGE')"
  >
    <template #header>
      <div class="flex items-center gap-2 flex-row flex-wrap min-w-0">
        <h5 class="mb-0 text-n-slate-12 font-medium text-lg break-words">
          {{ t('OVERVIEW_REPORTS.SLA_SUMMARY.HEADER') }}
        </h5>
      </div>
    </template>
    <div class="flex sm:flex-row flex-col flex-wrap items-center w-full gap-6">
      <DonutChart
        v-if="hasConversations"
        :data="gaugeData"
        :diameter="112"
        :thickness="14"
        :show-legend="false"
        :aria-label="t('SLA_REPORTS.METRICS.HIT_RATE.LABEL')"
        class="shrink-0"
      >
        <template #center>
          <span class="text-xl font-medium text-n-slate-12">
            {{ metrics.hitRate }}
          </span>
        </template>
      </DonutChart>
      <div
        class="flex sm:flex-row flex-col flex-wrap w-full gap-4 sm:gap-10 sm:border-s sm:border-n-strong sm:ps-6"
      >
        <SLAMetricCard
          :label="t('SLA_REPORTS.METRICS.HIT_RATE.LABEL')"
          :value="metrics.hitRate"
          :tool-tip="t('SLA_REPORTS.METRICS.HIT_RATE.TOOLTIP')"
          :is-loading="isLoading"
        />
        <div class="w-full sm:w-px bg-n-strong" />
        <SLAMetricCard
          :label="t('SLA_REPORTS.METRICS.NO_OF_MISSES.LABEL')"
          :value="metrics.numberOfSLAMisses"
          :tool-tip="t('SLA_REPORTS.METRICS.NO_OF_MISSES.TOOLTIP')"
          :is-loading="isLoading"
        />
        <div class="w-full sm:w-px bg-n-strong" />
        <SLAMetricCard
          :label="t('SLA_REPORTS.METRICS.NO_OF_CONVERSATIONS.LABEL')"
          :value="metrics.numberOfConversations"
          :tool-tip="t('SLA_REPORTS.METRICS.NO_OF_CONVERSATIONS.TOOLTIP')"
          :is-loading="isLoading"
        />
      </div>
    </div>
  </OverviewCard>
</template>
